import {
  Box,
  Button,
  Center,
  CenterProps,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactNode } from "react";

import { IcoError } from "@/common/components/Icons";

export type EmptyStateProps = Omit<CenterProps, "title" | "filter"> & {
  eyebrow?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  action?: ReactNode;
  isLoading?: boolean;
  showContent?: boolean;
  children?: ReactNode;
  icon?: ReactNode;
  maxW?: Pick<CenterProps, "maxW">;
  isError?: boolean;
  onRetry?: () => void;
};

const wrapTitle = (title: ReactNode) =>
  typeof title === "string" ? <Heading fontSize="xl">{title}</Heading> : title;

const wrapDescription = (description: ReactNode) =>
  typeof description === "string" ? <Text>{description}</Text> : description;

const wrapError = (error: ReactNode) =>
  typeof error === "string" ? <Text>{error}</Text> : error;

export default function EmptyState(props: EmptyStateProps) {
  const {
    children,
    eyebrow,
    title,
    description,
    error,
    action,
    isLoading = false,
    showContent = true,
    icon,
    maxW = "sm",
    isError,
    onRetry,
    ...centerProps
  } = props;
  return (
    <Center {...centerProps}>
      {isLoading ? null : isError ? (
        <VStack
          maxW={maxW}
          p={4}
          spacing={4}
          sx={{ textWrap: "balance" }}
          textAlign="center"
        >
          <IcoError boxSize="48px" color="veryDimmed" />
          {wrapError(error)}
          {onRetry && (
            <Button variant="outline" onClick={onRetry}>
              Retry
            </Button>
          )}
        </VStack>
      ) : showContent ? (
        <VStack
          maxW={maxW}
          p={4}
          spacing={4}
          sx={{ textWrap: "balance" }}
          textAlign="center"
        >
          {eyebrow}
          {icon && <Box color="veryDimmed">{icon}</Box>}
          {children}
          {wrapTitle(title)}
          {description && (
            <Box color="dimmed" fontSize="sm">
              {wrapDescription(description)}
            </Box>
          )}
          {action}
        </VStack>
      ) : null}
    </Center>
  );
}
